/**
 * The table of countries a workspace's money settings start from (L42 and
 * L43 of docs/plans/ledger.md): where the financial year starts, whether
 * companies there choose their own year, the tax a registered business
 * charges, and that tax's rates.
 *
 * Data, not code. OS reads a row during setup to set
 * `app.tenant.fiscal_year_start` and `app.tenant.tax_regime`, and a form
 * reads the rates to check a rate the person picked. A rate is never set on a
 * line from here: the rate is the line's own field. India's list is
 * `GST_RATES` (`gst-rates.ts`), so the two cannot disagree.
 *
 * A country that is not in the table has no assumed year (OS asks) and no
 * tax list. Calendars that are not Gregorian (Nepal, Iran, Ethiopia) are an
 * open item of the spec, so their rows are left out.
 *
 * The rates are the standard rate first, then the reduced ones, then zero.
 * They are percentages as decimal strings, like `GST_RATES`, and are read in
 * basis points through `rateBasisPoints`.
 */
import { GST_RATES, rateBasisPoints } from './gst-rates';

/** How tax is kept for a workspace (L43). */
export const TAX_REGIMES = ['none', 'in_gst', 'single', 'sales_tax'] as const;
export type TaxRegime = (typeof TAX_REGIMES)[number];

export const isTaxRegime = (value: unknown): value is TaxRegime => (TAX_REGIMES as readonly unknown[]).includes(value);

/** The day a financial year starts on: a month (1 to 12) and a day of it. */
export type FiscalYearStart = { readonly month: number; readonly day: number };

/** What the country's tax is called, so OS asks "Are you registered for GST?" or "for VAT?". */
export type TaxName = 'gst' | 'vat' | 'sales_tax' | 'consumption_tax';

export type TaxCountry = {
  /** ISO 3166-1 alpha-2. */
  readonly iso: string;
  /** Where the financial year starts for most businesses there. */
  readonly fiscalYearStart: FiscalYearStart;
  /** Companies there choose their own year (the UK, the US, Canada): OS asks, with the start above as its suggestion. */
  readonly yearChosen: boolean;
  /** The tax a registered business charges, and its name; `none` where there is no such tax. */
  readonly regime: TaxRegime;
  readonly taxName: TaxName | null;
  /** The country's rates, standard first. Empty under `none`, and under `sales_tax`, where the rate is set by state and city. */
  readonly rates: readonly string[];
};

const APRIL: FiscalYearStart = { month: 4, day: 1 };
const JULY: FiscalYearStart = { month: 7, day: 1 };
const JANUARY: FiscalYearStart = { month: 1, day: 1 };

const vat = (iso: string, rates: readonly string[], start: FiscalYearStart = JANUARY): TaxCountry => ({
  iso,
  fiscalYearStart: start,
  yearChosen: false,
  regime: 'single',
  taxName: 'vat',
  rates,
});

/** India's list, standard first: the same rates as `GST_RATES`. */
const INDIA_RATES: readonly string[] = ['18', ...GST_RATES.filter((r) => r !== '18' && r !== '0').reverse(), '0'];

export const TAX_COUNTRIES: readonly TaxCountry[] = [
  { iso: 'IN', fiscalYearStart: APRIL, yearChosen: false, regime: 'in_gst', taxName: 'gst', rates: INDIA_RATES },
  // The Gulf: VAT from 1 January.
  vat('AE', ['5', '0']),
  vat('SA', ['15', '0']),
  vat('OM', ['5', '0']),
  vat('BH', ['10', '0']),
  { iso: 'QA', fiscalYearStart: JANUARY, yearChosen: false, regime: 'none', taxName: null, rates: [] },
  { iso: 'KW', fiscalYearStart: JANUARY, yearChosen: false, regime: 'none', taxName: null, rates: [] },
  // Companies choose their own year.
  { iso: 'GB', fiscalYearStart: APRIL, yearChosen: true, regime: 'single', taxName: 'vat', rates: ['20', '5', '0'] },
  { iso: 'US', fiscalYearStart: JANUARY, yearChosen: true, regime: 'sales_tax', taxName: 'sales_tax', rates: [] },
  { iso: 'CA', fiscalYearStart: JANUARY, yearChosen: true, regime: 'single', taxName: 'gst', rates: ['5', '13', '15', '0'] },
  // GST with input credit, one tax.
  { iso: 'AU', fiscalYearStart: JULY, yearChosen: false, regime: 'single', taxName: 'gst', rates: ['10', '0'] },
  { iso: 'NZ', fiscalYearStart: APRIL, yearChosen: false, regime: 'single', taxName: 'gst', rates: ['15', '0'] },
  { iso: 'SG', fiscalYearStart: JANUARY, yearChosen: false, regime: 'single', taxName: 'gst', rates: ['9', '0'] },
  { iso: 'JP', fiscalYearStart: APRIL, yearChosen: false, regime: 'single', taxName: 'consumption_tax', rates: ['10', '8', '0'] },
  // South Asia.
  { iso: 'PK', fiscalYearStart: JULY, yearChosen: false, regime: 'single', taxName: 'gst', rates: ['18', '0'] },
  vat('BD', ['15', '10', '7.5', '5', '0'], JULY),
  vat('LK', ['18', '0'], APRIL),
  // Africa.
  vat('EG', ['14', '0'], JULY),
  vat('KE', ['16', '8', '0'], JULY),
  vat('ZA', ['15', '0'], { month: 3, day: 1 }),
  vat('NG', ['7.5', '0']),
  // Europe: VAT, and the calendar year.
  vat('DE', ['19', '7', '0']),
  vat('FR', ['20', '10', '5.5', '2.1', '0']),
  vat('IT', ['22', '10', '5', '4', '0']),
  vat('ES', ['21', '10', '4', '0']),
  vat('NL', ['21', '9', '0']),
  vat('BE', ['21', '12', '6', '0']),
  vat('IE', ['23', '13.5', '9', '0']),
  vat('AT', ['20', '13', '10', '0']),
  vat('PT', ['23', '13', '6', '0']),
  vat('SE', ['25', '12', '6', '0']),
  vat('DK', ['25', '0']),
  vat('PL', ['23', '8', '5', '0']),
  vat('FI', ['25.5', '14', '10', '0']),
  vat('GR', ['24', '13', '6', '0']),
  // Hong Kong charges no sales tax; its year starts in April.
  { iso: 'HK', fiscalYearStart: APRIL, yearChosen: false, regime: 'none', taxName: null, rates: [] },
];

const BY_ISO: ReadonlyMap<string, TaxCountry> = new Map(TAX_COUNTRIES.map((c) => [c.iso, c]));

/** The country's row, or null when the table has none. */
export const taxCountryOf = (iso: string | null | undefined): TaxCountry | null =>
  typeof iso === 'string' ? (BY_ISO.get(iso.trim().toUpperCase()) ?? null) : null;

/** The country's rates as decimal strings, standard first; empty when the table has none. */
export const taxRatesOf = (iso: string | null | undefined): readonly string[] => taxCountryOf(iso)?.rates ?? [];

/** The country's standard rate, or null. */
export const standardTaxRateOf = (iso: string | null | undefined): string | null => taxRatesOf(iso)[0] ?? null;

/** True when the rate (text or a number, `'5'`, `5`, `'5%'`) is on the country's list. */
export const isTaxRateOf = (iso: string | null | undefined, rate: string | number | null | undefined): boolean => {
  const bp = rateBasisPoints(rate);
  return bp !== null && taxRatesOf(iso).some((r) => rateBasisPoints(r) === bp);
};

/** The country a currency belongs to, where only one does. The euro and the dollar of several countries name none. */
const CURRENCY_COUNTRY: Readonly<Record<string, string>> = {
  INR: 'IN', AED: 'AE', SAR: 'SA', OMR: 'OM', BHD: 'BH', QAR: 'QA', KWD: 'KW',
  GBP: 'GB', USD: 'US', CAD: 'CA', AUD: 'AU', NZD: 'NZ', SGD: 'SG', JPY: 'JP',
  PKR: 'PK', BDT: 'BD', LKR: 'LK', EGP: 'EG', KES: 'KE', ZAR: 'ZA', NGN: 'NG',
  SEK: 'SE', DKK: 'DK', PLN: 'PL', HKD: 'HK',
};

/** A tax registration number's country: a UAE TRN starts 100, a Saudi VAT number starts and ends with 3. */
const trnCountry = (trn: string): string | null => {
  const digits = trn.replace(/\D/g, '');
  if (digits.length !== 15) return null;
  if (digits.startsWith('100')) return 'AE';
  if (digits.startsWith('3') && digits.endsWith('3')) return 'SA';
  return null;
};

/**
 * Where the business is, for its money settings: a GSTIN says India and a TRN
 * its Gulf country, then the country the person or their device gave, then a
 * currency only one country uses. Null when nothing says.
 */
export const countryOfBusiness = (input: {
  readonly gstin?: string | null;
  readonly trn?: string | null;
  /** ISO 3166-1 alpha-2: the country the person named, or their device's. */
  readonly region?: string | null;
  readonly currency?: string | null;
}): string | null => {
  if (typeof input.gstin === 'string' && input.gstin.trim() !== '') return 'IN';
  const region = typeof input.region === 'string' && /^[A-Za-z]{2}$/.test(input.region.trim()) ? input.region.trim().toUpperCase() : null;
  if (typeof input.trn === 'string' && input.trn.trim() !== '') {
    const fromTrn = trnCountry(input.trn);
    if (region !== null && taxCountryOf(region)?.regime === 'single') return region;
    if (fromTrn !== null) return fromTrn;
  }
  if (region !== null) return region;
  const currency = typeof input.currency === 'string' ? input.currency.trim().toUpperCase() : '';
  return CURRENCY_COUNTRY[currency] ?? null;
};
